import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e85a7b3-7844-4782-8600-31defd3a9a43/artifacts/5fhyey8q_MV%20trasporti%20logo.png";

export default function Footer() {
  const { t } = useTranslation();
  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/servizi", label: t("nav.services") },
    { to: "/chi-siamo", label: t("nav.about") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/preventivo", label: t("nav.quote") },
    { to: "/tracking", label: t("nav.client_area") },
    { to: "/contatti", label: t("nav.contact") },
  ];

  return (
    <>
      <footer className="bg-[#09090B] text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-1">
              <img
                src={LOGO_URL}
                alt="MV Trasporti"
                className="h-12 w-auto invert mb-6"
              />
              <p className="text-zinc-400 leading-relaxed font-body">
                {t("footer.company")}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-widest uppercase mb-6 text-[#FF5200] font-heading">
                {t("footer.useful_links")}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      data-testid={`footer-link-${link.to.replace("/", "") || "home"}`}
                      className="text-zinc-400 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-widest uppercase mb-6 text-[#FF5200] font-heading">
                {t("footer.contact")}
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                  <div className="text-zinc-400">
                    <p>+39 334 770 8108</p>
                    <p>+39 389 785 6034</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                  <a href="mailto:info@mvtrasporti.com" className="text-zinc-400 hover:text-white transition-colors duration-200">info@mvtrasporti.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-400">Piazza Ugo Dallò, 23<br />46043 Castiglione Delle Stiviere (MN)</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-widest uppercase mb-6 text-[#FF5200] font-heading">
                {t("footer.hours") || "Orari"}
              </h4>
              <div className="flex items-start gap-3 text-zinc-400">
                <Clock size={18} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p>{t("footer.hours_weekday") || "Lun - Ven: 08:00 - 18:00"}</p>
                  <p>{t("footer.hours_sat") || "Sab: 08:00 - 13:00"}</p>
                  <p>{t("footer.hours_sun") || "Dom: Chiuso"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City SEO Links */}
        <div className="border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
            <p className="text-xs font-bold tracking-widest uppercase text-zinc-600 mb-4 font-heading">
              {t("footer.city_seo") || "Trasporti per citta"}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {["Milano", "Brescia", "Verona", "Bergamo", "Mantova", "Padova", "Vicenza", "Roma", "Torino", "Bologna", "Firenze", "Napoli"].map((c) => (
                <Link key={c} to={`/trasporti/${c.toLowerCase()}`} className="text-xs text-zinc-500 hover:text-[#FF5200] transition-colors">
                  {t("footer.transport_to") || "Trasporti"} {c}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-sm">
              &copy; {new Date().getFullYear()} MV Trasporti. {t("footer.rights")}.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy" className="text-zinc-500 hover:text-[#FF5200] transition-colors" data-testid="footer-privacy-link">Privacy &amp; Cookie Policy</Link>
              <p className="text-zinc-500">P.IVA: IT02681700205</p>
            </div>
          </div>
        </div>
      </footer>

      <a
        href="https://wa.me/3347708108"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 right-4 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-transform duration-300 z-40"
        data-testid="whatsapp-button"
        aria-label="Contattaci su WhatsApp"
      >
        <MessageCircle size={28} />
      </a>
    </>
  );
}
